import React, {FunctionComponent, useCallback, useEffect, useMemo, useState} from 'react'
import styles from './RefEditDialog.module.sass'
import {Dialog} from '../../../etc/Dialog/Dialog'
import {DefaultTaskUser, RefIntersection, WorkType} from '../../../../responses/refs/refs'
import Label, {Display} from '../../../controls/Label/Label'
import {
    buildMergeCallback,
    createIdGenerator,
    defaultValue,
    fromBool,
    mergeObject,
    modifyProp,
    noop,
    setProp,
    snoc,
    toBool,
    unit
} from '../../../../utils/utils'
import {flow} from 'fp-ts/es6/function'
import OkCancel from '../../../controls/OkCancel/OkCancel'
import {RefEditState} from '../../../../redux/pages/settings/refs'
import Field from '../../../controls/Field/Field'
import {EMPTY_OBJECT_TYPE, ObjectType} from '../../../../responses/objects'
import clsx from 'clsx'
import {entityRemove, entityReplace, getId} from '../../../../utils/entity-utils'
import TextArea, {Resize} from '../../../controls/TextArea/TextArea'
import TagEditor from '../../../data-components/tags/TagEditor/TagEditor'
import Button, {Size} from '../../../controls/Button/Button'
import {Tag} from '../../../../responses/tags'
import ColorPicker from '../../../data-components/pickers/ColorPicker/ColorPicker'
import {refsConfig} from '../RefPage/refs-config'
import {isNonEmpty} from 'fp-ts/es6/Array'
import {apiRequests} from '../../../../http/config'
import {fromQueryWithReset} from '../../../../utils/rx-operators'
import DeleteIcon from 'files/delete.svg'
import PlusIcon from 'files/plus.svg'
import CloseIcon from 'files/close.svg'
import MaxLength from '../../../controls/MaxLength/MaxLength'
import {Checkbox} from '../../../controls/Checkbox/Checkbox'
import {ADTType} from '@morphic-ts/adt'
import Dropdown from '../../../data-components/dropdowns/Dropdown/Dropdown'
import {useObservableState} from 'observable-hooks'
import RequestDropdown from '../../../data-components/dropdowns/RequestDropdown/RequestDropdown'
import {Departments} from '../../../../responses/departments'
import {User} from '../../../../responses/users'
import {ID} from '../../../../types/UtilityTypes'
import {errorToast} from '../../../controls/Toaster/Toaster'

const idGenerator = createIdGenerator()

const checkItemWorkType = (checkItem:DefaultTaskUser[]|undefined, id:ID) => {
    const result = checkItem?.find(item => item.id == id)
    return result?.work_type || undefined
}
const checkItemUser = (checkItem:DefaultTaskUser[]|undefined, id:ID) => {
    const result = checkItem?.find(item => item.id == id)
    return result?.user || undefined
}

const getNewItemID = (items:DefaultTaskUser[]) => {
    const itemsIDs = items?.map(item => item.id) || []
    let newID:any = -1 // eslint-disable-line

    if(itemsIDs.length > 0) {
        let min:any = itemsIDs[0] // eslint-disable-line
        let max = min
        for (let i = 1; i < itemsIDs.length; ++i) {
            if (itemsIDs[i] > max) max = itemsIDs[i]
            if (itemsIDs[i] < min) min = itemsIDs[i]
        }
        min > 0 ?
            newID = -1 :
            newID = min - 1
    }
    return newID
}

const checkArrayRepeat = (array:any[]) => { // eslint-disable-line
    const newArr = array.map(item => item.work_type?.id)
    let x = false
    const result = newArr.reduce(function (acc, el){
        if(el != undefined && typeof el === 'number') {
            acc[el] = (acc[el] || 0) + 1
        }
        return acc
    }, {})

    for( const key in result) {
        result[key] > 1 ? x = true : x = false
        if(x) {
            const projectTypeName = array.find(item => {
                if( item.work_type.id == key) {
                    return item
                }
            })
            const errorText = `Для типа проекта "${projectTypeName.work_type.code + ' - ' + projectTypeName.work_type.name}" выбрано более одного исполнителя`
            errorToast(errorText)
            break
        }
    }
    return x
}
const generateType = (name = ''): ObjectType => ({
    ...EMPTY_OBJECT_TYPE,
    id: idGenerator.next().value,
    name,
})

interface Props {
    state: ADTType<typeof RefEditState>,
    onOk?: (row: Partial<RefIntersection>) => void,
    onCancel?: () => void,
}

const RefEditDialog: FunctionComponent<Props> = props => {
    const [ref, setRef] = useState<Partial<RefIntersection>>({})
    const reference = useMemo(() =>
        RefEditState.isAnyOf(['create', 'update'])(props.state) ? refsConfig[props.state.key]: undefined,
        [props.state]
    )

    useEffect(() => {
        RefEditState.match({
            update: ({ref}) => setRef(ref),
            create: ({key}) => setRef({
                counter: key === 'work-type' ? 0 : undefined,
                types: key === 'work-object' ?
                    [generateType('Разработка'), generateType('Адаптация')] :
                    undefined,
            })
        }, noop)(props.state)
    }, [props.state])

    const [categories, searchCategories] = useObservableState(fromQueryWithReset(query => apiRequests.dictionary.list({
        params: { key: 'work-object-category' },
        query
    })), [])

    const updateRef = useCallback((changes: Partial<RefIntersection>) => setRef(mergeObject<Partial<RefIntersection>>(changes)), [])

    const updateTags = useCallback((modifier: (tags: Tag[]) => Tag[]) => setRef(modifyProp('tags', flow(
        defaultValue([] as Tag[]),
        modifier,
    ))), [])

    const updateTypes = useCallback((modifier: (tags: ObjectType[]) => ObjectType[]) => setRef(modifyProp('types', flow(
        defaultValue<ObjectType[]>([]),
        modifier,
    ))), [])

    return <>
        {
            RefEditState.isAnyOf(['create','update', 'delete'])(props.state) &&
            <Dialog
                className={clsx(styles.dialog, styles[reference?.key ?? ''] ?? styles.default)}
                header={props.state.type === 'update' ? 'Редактирование' : 'Добавление'}
                onClose={props.onCancel}
                actions={({ onClose }) => <OkCancel
                    onOk={() => props.onOk?.(ref)}
                    onCancel={onClose}
                    disabled={
                        ref.executors && ref.executors?.length > 0
                            ? ref.executors.filter(item => item.work_type === undefined || item.user === undefined).length > 0 || checkArrayRepeat(ref.executors)
                                ? true
                                : false
                            : false
                    }
                />}
            >
                {
                    reference?.key === 'work-object' ? <>
                            <Label
                                className={styles.line}
                                display={Display.TOP}
                                label={<MaxLength max={40} value={ref.name}>Название *</MaxLength>}
                            >
                                <Field block maxLength={40} type={'text'} value={ref.name} onChange={name => updateRef({ name })}/>
                            </Label>
                            <Label label={'Категория *'} display={Display.TOP}>
                                <Dropdown
                                    toKey={getId}
                                    data={categories}
                                    content={(selected, render) => selected.map(render).join()}
                                    selected={unit(ref.category)}
                                    render={category => category.name}
                                    onClick={category => updateRef({ category })}
                                    onChange={(search, reset) => searchCategories({search, reset})}
                                />
                            </Label>
                            <Label className={styles.description} label={'Описание'} display={Display.TOP}>
                                <TextArea
                                    block
                                    value={ref.description}
                                    onChange={description => updateRef({ description })}
                                />
                            </Label>
                            <Label label={'Папка *'} display={Display.TOP}>
                                <Field block type={'text'} value={ref.folder} onChange={folder => updateRef({ folder })}/>
                            </Label>
                            <TagEditor
                                editable
                                className={styles.line}
                                tags={ref.tags ?? []}
                                constantTags={[]}
                                onAdd={name => updateTags(snoc({ id: idGenerator.next().value, name }))}
                                onDelete={tag => updateTags(entityRemove(tag))}
                            />
                            {
                                isNonEmpty(ref.types ?? []) && <div className={styles.table}>
                                    <table>
                                        <thead>
                                        <tr>
                                            <th colSpan={2}>Тип объекта</th>
                                            <th>Стоимость (₽)</th>
                                            <th colSpan={2}>Дни</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {
                                            ref.types?.map((type, index) => <tr key={type.id}>
                                                <td>{index + 1}.</td>
                                                <td><Field
                                                    block
                                                    value={type.name}
                                                    onChange={buildMergeCallback(type, 'name', flow(entityReplace, updateTypes))}
                                                /></td>
                                                <td><Field
                                                    block
                                                    type={'number'}
                                                    value={type.cost}
                                                    onChange={buildMergeCallback(type, 'cost', flow(entityReplace, updateTypes))}
                                                /></td>
                                                <td><Field
                                                    block
                                                    value={type.date_range}
                                                    onChange={buildMergeCallback(type, 'date_range', flow(entityReplace, updateTypes))}
                                                />
                                                </td>
                                                <td><Button onClick={() => updateTypes(entityRemove(type))}>
                                                    <DeleteIcon/>
                                                </Button></td>
                                            </tr>)
                                        }
                                        </tbody>
                                    </table>
                                </div>
                            }
                            <Button onClick={() => updateTypes(snoc(generateType()))}>
                                <PlusIcon/> Добавить тип
                            </Button>
                        </> :
                        reference?.key === 'task-type' ? <>
                            {/*<Dropdown
                                block
                                data={[KindType.DESIGN, KindType.PRODUCTION]}
                                placeholder={'Тип'}
                                selected={unit(ref?.kind)}
                                toKey={identity}
                                render={kindToString}
                                content={() => ref?.kind && kindToString(ref.kind)}
                                onClick={kind => setRef(setProp('kind', kind))}
                            />*/}
                            <div className={styles.wrapper}>
                                <Label label={'Название *'} display={Display.TOP}>
                                    <Field
                                        block
                                        type={'text'}
                                        value={ref.name}
                                        onChange={name => updateRef({ name })}
                                    />
                                </Label>
                                <Label
                                    label={<MaxLength max={3} value={ref.short_name}>Тег *</MaxLength>}
                                    display={Display.TOP}
                                >
                                    <Field
                                        block
                                        maxLength={3}
                                        type={'text'}
                                        value={ref.short_name}
                                        onChange={short_name => updateRef({ short_name })}
                                    />
                                </Label>
                            </div>
                            <div className={styles.wrapper}>
                                <RequestDropdown<Departments>
                                    type={'multi'}
                                    placeholder={'Отделы'}
                                    value={ref.departments ?? []}
                                    render={department => department.title}
                                    request={query => apiRequests.dictionary.departmentList({
                                        params: { key: 'department' },
                                        query
                                    })}
                                    onChange={departments => updateRef({departments})}
                                />
                                <ColorPicker
                                    value={ref.color}
                                    text={ref.short_name}
                                    selected={[]}
                                    onChange={color => updateRef({ color })}
                                />
                            </div>
                            <div className={styles.wrapper}>
                                <Label label={'Папка'} display={Display.TOP}>
                                    <Field
                                        block
                                        placeholder={'Название папки задачи на латинице'}
                                        type={'text'}
                                        value={ref.folder}
                                        onChange={folder => updateRef({ folder })}
                                    />
                                </Label>
                            </div>
                            <div className={styles.mw_wrapper}>
                                <Label label={'Чек-лист для задачи'} display={Display.TOP}>
                                    <TextArea
                                        className={styles.textarea}
                                        placeholder={'Опишите чек-лист для задачи'}
                                        value={ref.checklist}
                                        resize={Resize.VERTICAL}
                                        onChange={checklist => updateRef({ checklist })}
                                    />
                                </Label>
                            </div>
                            <div className={styles.wrapper}>
                                <Label label={'Тендерный'} display={Display.TOP}>
                                    <Checkbox
                                        className={styles.checkBox}
                                        value={fromBool(ref.can_tendered || 0)}
                                        onChange={value => updateRef({ can_tendered: toBool(value) })}
                                    />
                                </Label>
                                <Label label={'Сохранять файлы'} display={Display.TOP}>
                                    <Checkbox
                                        className={styles.checkBox}
                                        value={fromBool(ref.keep_files || 0)}
                                        onChange={value => updateRef({ keep_files: toBool(value) })}
                                    />
                                </Label>
                            </div>
                            <div className={styles.mw_wrapper}>
                                <Label label={'Настроить автоматическую постановку задач'} display={Display.TOP}>
                                    {
                                        ref.executors?.map(executor => <div className={styles.userTaskAdd} key={executor.id}>
                                                <RequestDropdown<WorkType>
                                                    className={styles.userTaskAddDropdown}
                                                    type={'single'}
                                                    placeholder={'Тип проекта'}
                                                    value={checkItemWorkType(ref.executors, executor.id)}
                                                    render={work_type => {
                                                        return work_type.code + ' - ' + work_type.name
                                                    }}
                                                    request={query => apiRequests.dictionary.list({
                                                        params: {key: 'work-type'},
                                                        query
                                                    })}
                                                    onChange={(work_type) => {
                                                        const newArr = ref.executors?.slice() || []
                                                        if(newArr.length > 0) {
                                                            const itemIndex = newArr.findIndex(e => e.id == executor.id)
                                                            newArr[itemIndex].work_type = work_type
                                                            updateRef({executors: newArr})
                                                        }
                                                    }
                                                    }
                                                />
                                                <RequestDropdown<User>
                                                    className={styles.userTaskAddDropdown}
                                                    type={'single'}
                                                    placeholder={'Исполнитель'}
                                                    value={checkItemUser(ref.executors, executor.id)}
                                                    render={user => user.name}
                                                    request={query => apiRequests.users.list({
                                                        query
                                                    })}
                                                    onChange={(user) => {
                                                        const newArr = ref.executors?.slice() || []
                                                        if(newArr.length > 0) {
                                                            const itemIndex = newArr.findIndex(e => e.id == executor.id)
                                                            newArr[itemIndex].user = user
                                                            updateRef({executors: newArr})
                                                        }
                                                    }
                                                    }
                                                />
                                                <Button
                                                    size={Size.NORMAL_ICON}
                                                    onClick={()=>{
                                                        const newArr = ref.executors?.slice() || []
                                                        const newArray = newArr.filter(item => item.id !== executor.id)
                                                        updateRef({executors: newArray})
                                                    }}
                                                    justify={'flex-start'}
                                                    className={styles.closeButton}>
                                                    <CloseIcon />
                                                </Button>
                                            </div>
                                        )
                                    }
                                    <div>

                                    </div>
                                    <Button size={Size.NORMAL_ICON} onClick={
                                        () => {
                                            const newArr = ref.executors?.slice() || []
                                            newArr.push({
                                                id: getNewItemID(newArr || []),
                                                work_type: undefined,
                                                user: undefined
                                            })
                                            updateRef({executors: newArr})
                                        }
                                    }
                                    justify={'flex-start'} className={styles.add_line_button}>
                                        <PlusIcon className={styles.borderPlusIcon} /> Добавить тип проекта и исполнителя
                                    </Button>
                                </Label>
                            </div>
                        </> : reference?.columns.map((column, index) => <Label
                            key={column.key}
                            label={column.name + (column.required ? ' *' : '')}
                            display={Display.TOP}
                        >
                            {
                                (column.type === 'text') && <Field
                                    block
                                    type={'text'}
                                    autoFocus={index === 0}
                                    value={ref[column.key]}
                                    onChange={value => setRef(setProp(column.key, value))}
                                    readonly={reference?.key === 'client' && column.key === 'short_title'}
                                />
                            }
                            {
                                (column.type === 'number') && <Field
                                    block
                                    type={'number'}
                                    autoFocus={index === 0}
                                    value={ref[column.key]}
                                    min={0}
                                    onChange={value => setRef(setProp(column.key, value))}
                                />
                            }
                            {
                                (column.type === 'boolean') && <Checkbox
                                    className={styles.checkBox}
                                    value={!!ref[column.key]}
                                    onChange={value => setRef(setProp(column.key, toBool(value)))}
                                />
                            }

                        </Label>)
                }
            </Dialog>
        }
    </>
}

export default RefEditDialog